import React from 'react';
import { BarChart, Trash2, RefreshCw } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов НДФЛ
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты расчета НДФЛ для сравнения.
        </p>
      </div>
    );
  }

  // Получаем имя типа дохода
  const getIncomeTypeName = (typeId) => {
    const incomeTypes = {
      'salary': 'Зарплата',
      'business': 'Бизнес',
      'dividend': 'Дивиденды',
      'foreign_dividend': 'Иностранные дивиденды',
      'interest': 'Проценты',
      'rent': 'Аренда',
      'sale_property': 'Продажа имущества',
      'gift': 'Подарки',
      'foreign_income': 'Доход из-за рубежа',
      'other': 'Прочие доходы'
    };
    return incomeTypes[typeId] || typeId;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'вариант' : savedCalculations.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<RefreshCw className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Доход:</span>
                <span className="font-medium">
                  {formatCurrency(calc.results.grossIncome, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип:</span>
                <span>
                  {getIncomeTypeName(calc.formData.incomeType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ставка:</span>
                <span>
                  {formatNumber(calc.results.taxRate)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span>НДФЛ:</span>
                <span className="font-semibold text-rose-600 dark:text-rose-400">
                  {formatCurrency(calc.results.taxAmount, 0)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonSidebar;